import React from 'react';

import { observer } from 'mobx-react';

import { Checkbox, FormControlLabel, GridLegacy as Grid, Typography } from '@mui/material';

import Record from 'ui/Record/Record';

import { Button } from 'components/Button';
import MainHeader from 'components/MainHeader';
import FieldHelpIcon from 'components/FieldHelpIcon';
import TimezonePicker from 'components/TimezonePicker';

import routes from 'core/routes';

import { UserTimezoneUpdate } from 'utils/api/modules/user/user.types';
import { useFocusAfterSave } from 'utils/hooks/useFocusAfterSave';
import { useNavBarHeight } from 'utils/hooks/navbar';
import { useRetryableLoad } from 'utils/hooks/useRetryableLoad';
import { useStore } from 'utils/hooks/store';

import {
  HeaderGrid,
  TaskRecordContainer,
  TaskRecordRow,
  Title,
} from 'containers/CompanySettings/MasterServiceAgreement/styles';

import {
  FieldColumn,
  FieldRow,
  FieldSkeleton,
  LabelColumn,
  LoadError,
  PreferenceField,
  SettingsSection,
} from './styles';

const HELP_TEXT =
  'Times across Vatix are shown in this timezone. Turn the automatic setting off to choose one yourself.';

const TimezoneSection: React.FunctionComponent = observer(() => {
  const { timezone, timezoneSettings } = useStore();
  const { draft, status, saving, error } = timezoneSettings.personal;
  const checkboxRef = React.useRef<HTMLInputElement>(null);
  const pickerRef = React.useRef<HTMLInputElement>(null);
  const focusAfterSave = useFocusAfterSave(saving);

  const retryLoad = useRetryableLoad(timezoneSettings.loadPersonal);

  const followsDevice = draft?.timezoneFollowsDevice ?? true;
  const shownZone = !followsDevice && draft?.timezone ? draft.timezone : timezone.readerZone;

  const save = (control: React.RefObject<HTMLInputElement>, update: UserTimezoneUpdate): void => {
    focusAfterSave(control);
    timezoneSettings.savePersonal(update);
  };

  const handleFollowDevice = (checked: boolean): void =>
    save(
      checkboxRef,
      checked ? { timezoneFollowsDevice: true } : { timezoneFollowsDevice: false, timezone: shownZone }
    );

  let helperText: React.ReactNode = null;
  if (error) {
    helperText = <span data-testid="preferences-timezone-error">{error}</span>;
  } else if (!followsDevice && !draft?.timezone) {
    helperText = <span data-testid="preferences-timezone-helper">Your organisation&apos;s default</span>;
  }

  return (
    <SettingsSection data-testid="preferences-timezone-section">
      <HeaderGrid container alignItems="center">
        <Grid item xs={12} container alignContent="flex-start">
          <Title>Timezone</Title>
        </Grid>
      </HeaderGrid>
      <TaskRecordContainer>
        <TaskRecordRow>
          <FieldRow>
            <LabelColumn>
              Your timezone
              <FieldHelpIcon name="Your timezone" helpText={HELP_TEXT} />
            </LabelColumn>
            <FieldColumn>
              {status === 'loading' && <FieldSkeleton variant="rounded" />}
              {status === 'failed' && (
                <LoadError role="alert">
                  <Typography variant="body2" color="text.secondary">
                    Your timezone setting could not be loaded
                  </Typography>
                  <Button variant="outlined" size="small" onClick={retryLoad}>
                    Try again
                  </Button>
                </LoadError>
              )}
              {status === 'ready' && (
                <PreferenceField>
                  <TimezonePicker
                    value={shownZone}
                    onChange={(zone) => save(pickerRef, { timezoneFollowsDevice: false, timezone: zone })}
                    ariaLabel="Your timezone"
                    disabled={followsDevice || saving}
                    error={!!error}
                    helperText={helperText}
                    inputRef={pickerRef}
                    data-testid="preferences-timezone-dropdown"
                  />
                  <FormControlLabel
                    label="Set timezone automatically from this device"
                    control={
                      <Checkbox
                        size="small"
                        checked={followsDevice}
                        disabled={saving}
                        inputRef={checkboxRef}
                        onChange={(event) => handleFollowDevice(event.target.checked)}
                      />
                    }
                  />
                </PreferenceField>
              )}
            </FieldColumn>
          </FieldRow>
        </TaskRecordRow>
      </TaskRecordContainer>
    </SettingsSection>
  );
});

const Preferences: React.FunctionComponent = () => {
  const navBarHeight = useNavBarHeight();

  return (
    <Record
      Header={
        <MainHeader
          title="Preferences"
          breadcrumbs={[
            { label: 'Personal', link: '' },
            { label: 'Preferences', link: routes.dashboard.settings.preferences.toString() },
          ]}
        />
      }
      Content={() => <TimezoneSection />}
      navBarHeight={navBarHeight}
    />
  );
};

export default Preferences;
